import Header from "../components/header/Header";
import { Table, Button, Input, Space, Modal, Form, DatePicker, message, Select, Tag, Divider, InputNumber, Radio } from "antd";
import { useEffect, useState, useRef } from "react";
import PrintInvoice from "../components/invoice/PrintInvoice";
import Highlighter from "react-highlight-words";
import { SearchOutlined } from "@ant-design/icons";
import { apiRequest } from "../utils/apiClient";
import { useLocation } from "react-router-dom";
import dayjs from "dayjs";
import {
  INVOICE_PAGE_LIMIT,
  INVOICE_TYPE_POS,
  INVOICE_TYPE_DIGITAL_INVOICING,
  PROVINCES,
} from "../utils/constants";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import PrintPosPeriod from "../components/invoice/PrintPosPeriod";
import { setSelectedBranch } from "../redux/userSlice";

const InvoicePage = () => {
  const { t } = useTranslation("pages");
  const dispatch = useDispatch();
  const userRole = useSelector((state) => state.user?.role);
  const selectedBranchId = useSelector((state) => state.user?.selectedBranchId) || "";
  const isAdmin = userRole !== "cashier";

  const showNA = (value) => {
    if (value === null || value === undefined || value === "") {
      return t("na");
    }
    return value;
  };
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [printData, setPrintData] = useState({});
  const autoOpenedRef = useRef(false);
  const location = useLocation();
  const user = useSelector((state) => state.user);
  const isPosUser = user?.invoiceType === INVOICE_TYPE_POS;

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [editForm] = Form.useForm();
  const [editItems, setEditItems] = useState([]);

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: INVOICE_PAGE_LIMIT,
    total: 0,
  });

  const [invoiceNumberSearch, setInvoiceNumberSearch] = useState("");
  const searchInput = useRef(null);

  // POS: weekly/monthly printable summaries
  const [posPeriodPrintOpen, setPosPeriodPrintOpen] = useState(false);
  const [posPeriodLabel, setPosPeriodLabel] = useState("");
  const [posPeriodFromISO, setPosPeriodFromISO] = useState(null);
  const [posPeriodToISO, setPosPeriodToISO] = useState(null);
  const [posPeriodInvoices, setPosPeriodInvoices] = useState([]);
  const [weeklyLoading, setWeeklyLoading] = useState(false);
  const [monthlyLoading, setMonthlyLoading] = useState(false);
  const [customFrom, setCustomFrom] = useState(null);
  const [customTo, setCustomTo] = useState(null);
  const [customLoading, setCustomLoading] = useState(false);
  const [fbrFilterModalOpen, setFbrFilterModalOpen] = useState(false);
  const [pendingPeriodAction, setPendingPeriodAction] = useState(null);
  const [selectedFbrFilter, setSelectedFbrFilter] = useState("both");

  const fetchPosPeriodInvoices = async ({ fromISO, toISO, label, setLoading, fbrFilter = "both" }) => {
    if (!isPosUser) {
      message.warning(t("posOnlyMessage"));
      return;
    }

    try {
      setLoading(true);

      let url =
        `/api/invoices/get-all?page=1&limit=5000` +
        `&invoiceType=${encodeURIComponent(INVOICE_TYPE_POS)}` +
        `&from=${encodeURIComponent(fromISO)}` +
        `&to=${encodeURIComponent(toISO)}`;
      
      if (fbrFilter && fbrFilter !== "both") {
        url += `&fbrFilter=${encodeURIComponent(fbrFilter)}`;
      }
      if (isAdmin && selectedBranchId) {
        url += `&branchId=${encodeURIComponent(selectedBranchId)}`;
      }
      url += `&_t=${Date.now()}`;

      const res = await apiRequest(url);
      const items = Array.isArray(res?.data) ? res.data : [];

      // Print oldest -> newest for a better chronological report
      items.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));

      setPosPeriodLabel(label);
      setPosPeriodFromISO(fromISO);
      setPosPeriodToISO(toISO);
      setPosPeriodInvoices(items);
      setPosPeriodPrintOpen(true);
    } catch (error) {
      console.error(error);
      message.error(error?.message || t("failedPosPeriodInvoices"));
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateWeeklyPosInvoices = () => {
    // Today -> last 7 days (including today)
    const end = dayjs();
    const fromISO = end.subtract(6, "day").startOf("day").toISOString();
    const toISO = end.endOf("day").toISOString();
    
    setPendingPeriodAction({
      fromISO,
      toISO,
      label: "Weekly POS Invoices (Last 7 days)",
      setLoading: setWeeklyLoading,
    });
    setFbrFilterModalOpen(true);
  };

  const handleGenerateMonthlyPosInvoices = () => {
    // Today -> last 30 days (including today)
    const end = dayjs();
    const fromISO = end.subtract(29, "day").startOf("day").toISOString();
    const toISO = end.endOf("day").toISOString();
    
    setPendingPeriodAction({
      fromISO,
      toISO,
      label: "Monthly POS Invoices (Last 30 days)",
      setLoading: setMonthlyLoading,
    });
    setFbrFilterModalOpen(true);
  };

  const handleGenerateCustomPosInvoices = () => {
    if (!customFrom || !customTo) {
      message.warning(t("selectFromAndToDates"));
      return;
    }

    const from = dayjs(customFrom).startOf("day");
    const to = dayjs(customTo).endOf("day");

    if (to.isBefore(from)) {
      message.warning(t("toDateBeforeFrom"));
      return;
    }

    setPendingPeriodAction({
      fromISO: from.toISOString(),
      toISO: to.toISOString(),
      label: `POS Invoices (${from.format("YYYY-MM-DD")} to ${to.format("YYYY-MM-DD")})`,
      setLoading: setCustomLoading,
    });
    setFbrFilterModalOpen(true);
  };

  const handleFbrFilterConfirm = () => {
    if (!pendingPeriodAction) return;
    
    setFbrFilterModalOpen(false);
    fetchPosPeriodInvoices({
      ...pendingPeriodAction,
      fbrFilter: selectedFbrFilter,
    });
    setPendingPeriodAction(null);
    setSelectedFbrFilter("both"); // Reset to default
  };

  const handleFbrFilterCancel = () => {
    setFbrFilterModalOpen(false);
    setPendingPeriodAction(null);
    setSelectedFbrFilter("both"); // Reset to default
  };

  // 🔹 Fetch invoices (page-based, server-side pagination with search)
  const getInvoices = async (page = 1, pageSize = INVOICE_PAGE_LIMIT, search = "", bypassCache = false) => {
    try {
      setLoading(true);

      let url = `/api/invoices/get-all?page=${page}&limit=${pageSize}`;
      if (search) {
        url += `&search=${encodeURIComponent(search)}`;
      }
      if (isAdmin && selectedBranchId) {
        url += `&branchId=${encodeURIComponent(selectedBranchId)}`;
      }
      if (bypassCache) {
        url += `&_t=${Date.now()}`;
      }

      const res = await apiRequest(url);

      const safeData = (res.data || []).map((item) => ({
        ...item,
        key: item._id,
        invoiceNumber: item.invoiceNumber ?? "",
        customerName: item.customerName ?? "",
      }));

      setInvoices(safeData);

      if (res.pageInfo) {
        setPagination((prev) => ({
          ...prev,
          current: res.pageInfo.page || page,
          pageSize: res.pageInfo.pageSize || pageSize,
          total: res.pageInfo.total ?? prev.total,
        }));
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // 🔹 Initial fetch and refetch when branch changes
  useEffect(() => {
    if (location.state?.autoPrint && !location.state?.invoice) {
      setTimeout(() => {
        getInvoices(1, INVOICE_PAGE_LIMIT, invoiceNumberSearch, true);
      }, 1000);
    } else {
      getInvoices(1, INVOICE_PAGE_LIMIT, invoiceNumberSearch);
    }
  }, [selectedBranchId]);

  // 🔹 Handle auto-print and refresh when navigating from invoice creation
  useEffect(() => {
    if (!location.state?.autoPrint) return;
    if (autoOpenedRef.current) return;

    // Prefer the freshly created invoice passed from navigation state (after POST)
    const navInvoice = location.state?.invoice;
    if (navInvoice) {
      autoOpenedRef.current = true;
      setPrintData(navInvoice);
      setIsModalOpen(true);
      // Force refresh the list after a short delay to ensure cache invalidation completed
      // This ensures the latest invoice appears in the list
      setTimeout(() => {
        getInvoices(1, INVOICE_PAGE_LIMIT, invoiceNumberSearch, true);
      }, 1000);
      return;
    }

    // Fallback: use the latest invoice from the fetched list (sorted by createdAt desc on backend)
    if (invoices.length === 0) return;

    autoOpenedRef.current = true;
    setPrintData(invoices[0]);
    setIsModalOpen(true);
  }, [location.state?.autoPrint, location.state?.invoice, invoices]);

  // Recalculate totals from current edit items (mirrors backend logic for live preview)
  const getEditTotals = (items) => {
    if (!Array.isArray(items) || items.length === 0) return { subTotal: 0, tax: 0, totalAmount: 0 };
    const safeNum = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
    const subTotalBeforeDiscount = items.reduce(
      (sum, item) => sum + safeNum(item.price) * (Number(item.quantity) || 0),
      0
    );
    const totalProductDiscount = items.reduce((sum, item) => {
      const lineSub = safeNum(item.price) * (Number(item.quantity) || 0);
      const pct = safeNum(item.discount);
      return sum + (lineSub * pct) / 100;
    }, 0);
    const subTotal = Math.round((subTotalBeforeDiscount - totalProductDiscount) * 100) / 100;
    const tax = items.reduce((sum, item) => {
      const qty = Number(item.quantity) || 0;
      const price = safeNum(item.price);
      const taxRate = safeNum(item.taxRate);
      const discountPct = safeNum(item.discount);
      const lineSub = price * qty;
      const lineSubAfterDiscount = lineSub - (lineSub * discountPct) / 100;
      const lineTax = taxRate > 0 ? (lineSubAfterDiscount * taxRate) / 100 : safeNum(item.tax);
      return sum + lineTax;
    }, 0);
    const invoiceDiscount = safeNum(editingInvoice?.discount);
    const totalAmount = Math.max(0, Math.round((subTotal - invoiceDiscount + tax) * 100) / 100);
    return { subTotal, tax, totalAmount };
  };

  const openEditModal = (record) => {
    setEditingInvoice(record);
    setIsEditModalOpen(true);
    setEditItems(
      Array.isArray(record.cartItems)
        ? record.cartItems.map((item) => ({
            ...item,
            quantity: Number(item.quantity) || 0,
          }))
        : []
    );
    editForm.setFieldsValue({
      // Core fields
      invoiceNumber: record.invoiceNumber || "",
      invoiceDate: record.invoiceDate ? dayjs(record.invoiceDate) : (record.createdAt ? dayjs(record.createdAt) : null),
      dueDate: record.dueDate ? dayjs(record.dueDate) : null,
      paymentMode: record.paymentMode || "",
      // Customer basics
      customerName: record.customerName || "",
      customerPhoneNumber: record.customerPhoneNumber || "",
      // Digital invoicing / tax fields (if present on this invoice)
      customerCnicOrNtn: record.customerCnicOrNtn || "",
      businessName: record.businessName || "",
      salesTaxNumber: record.salesTaxNumber || "",
      province: record.province || undefined,
      salesTaxRegistration: record.salesTaxRegistration || undefined,
      challanNumber: record.challanNumber || "",
      customerAddress: record.customerAddress || "",
      editReason: record.lastEditReason || "",
    });
  };

  const handleEditSubmit = async () => {
    try {
      const values = await editForm.validateFields();

      if (!editingInvoice?._id) {
        message.error(t("missingInvoiceId"));
        return;
      }

      // Build safe items payload (drop zero-quantity rows)
      const safeItems = (editItems || [])
        .map((item) => ({
          ...item,
          quantity: Number(item.quantity) || 0,
        }))
        .filter((item) => item.quantity > 0);

      if (!safeItems.length) {
        message.warning(t("atLeastOneItemRequired"));
        return;
      }

      setLoading(true);

      const editReason = (values.editReason || "").trim().slice(0, 70);

      // 1) Update invoice metadata
      await apiRequest(`/api/invoices/update-invoice/${editingInvoice._id}`, {
        method: "PUT",
        body: JSON.stringify({
          // Core fields
          invoiceDate: values.invoiceDate ? values.invoiceDate.toISOString() : null,
          customerName: values.customerName?.trim() || "",
          customerPhoneNumber: values.customerPhoneNumber || "",
          paymentMode: values.paymentMode,
          dueDate: values.dueDate ? values.dueDate.toISOString() : null,
          // Digital invoicing / tax fields
          customerCnicOrNtn: values.customerCnicOrNtn || null,
          businessName: values.businessName || null,
          salesTaxNumber: values.salesTaxNumber || null,
          province: values.province || null,
          salesTaxRegistration: values.salesTaxRegistration || null,
          challanNumber: values.challanNumber || null,
          customerAddress: values.customerAddress || null,
          lastEditReason: editReason,
        }),
      });

      // 2) Update cart items & quantities (and reason so it persists with lastEditedAt)
      await apiRequest(`/api/invoices/update-invoice-items/${editingInvoice._id}`, {
        method: "PUT",
        body: JSON.stringify({
          cartItems: safeItems,
          lastEditReason: editReason,
        }),
      });

      message.success(t("invoiceUpdatedSuccess"));
      setIsEditModalOpen(false);
      setEditingInvoice(null);
      setEditItems([]);

      // Refresh list with current pagination & search
      getInvoices(pagination.current, pagination.pageSize, invoiceNumberSearch);
    } catch (error) {
      if (error?.errorFields) {
        // AntD form validation error – already shown in UI
        return;
      }
      console.error(error);
      message.error(error?.message || t("failedToUpdateInvoice"));
    } finally {
      setLoading(false);
    }
  };

  // 🔹 Server-side search handler for invoice number
  const handleInvoiceNumberSearch = (selectedKeys, confirm) => {
    const searchValue = selectedKeys[0] || "";
    setInvoiceNumberSearch(searchValue);
    confirm();
    // Reset to page 1 when searching
    getInvoices(1, pagination.pageSize, searchValue);
  };

  const handleInvoiceNumberReset = (clearFilters) => {
    clearFilters();
    setInvoiceNumberSearch("");
    // Reset search and fetch all invoices
    getInvoices(1, pagination.pageSize, "");
  };

  // 🔹 Search props for invoice number (server-side)
  const getInvoiceNumberSearchProps = () => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder={t("searchInvoiceNumber")}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => handleInvoiceNumberSearch(selectedKeys, confirm)}
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            type="primary"
            size="small"
            icon={<SearchOutlined />}
            style={{ width: 90 }}
            onClick={() => handleInvoiceNumberSearch(selectedKeys, confirm)}
          >
            {t("search")}
          </Button>
          <Button
            size="small"
            style={{ width: 90 }}
            onClick={() => clearFilters && handleInvoiceNumberReset(clearFilters)}
          >
            {t("reset")}
          </Button>
          <Button type="link" size="small" onClick={close}>
            {t("close")}
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
    ),
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
    render: (text) => {
      if (invoiceNumberSearch && text) {
        return (
          <Highlighter
            highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
            searchWords={[invoiceNumberSearch]}
            autoEscape
            textToHighlight={text.toString()}
          />
        );
      }
      return showNA(text);
    },
  });

  // 🔹 Table columns
  const columns = [
    {
      title: t("invoiceNo"),
      dataIndex: "invoiceNumber",
      key: "invoiceNumber",
      ...getInvoiceNumberSearchProps(),
    },
    {
      title: t("customerName"),
      dataIndex: "customerName",
      key: "customerName",
      render: (text) => showNA(text),
    },
    {
      title: t("createdDateAndTime"),
      dataIndex: "createdAt",
      key: "createdAt",
      render: (text) =>
        text ? new Date(text).toLocaleString("en-PK") : t("na"),
    },
    {
      title: t("lastEdited"),
      dataIndex: "lastEditedAt",
      key: "lastEditedAt",
      render: (text, record) => {
        const hasEdits =
          (record.lastEditedAt && record.createdAt && new Date(record.lastEditedAt).getTime() !== new Date(record.createdAt).getTime()) ||
          !!record.lastEditedAt;
        if (!hasEdits) {
          return <span className="text-gray-400 text-xs">{t("never")}</span>;
        }
        const value = text || record.lastEditedAt;
        return (
          <span className="text-xs">
            {new Date(value).toLocaleString("en-PK")}
          </span>
        );
      },
    },
    {
      title: t("paymentMethod"),
      dataIndex: "paymentMode",
      key: "paymentMode",
      render: (text) => showNA(text),
    },
    {
      title: t("totalPrice"),
      dataIndex: "totalAmount",
      key: "totalAmount",
      render: (text) => showNA(text),
    },
    {
      title: t("action"),
      key: "action",
      width: 120,
      fixed: "right",
      render: (_, record) => (
        <Space>
          <Button size="small" onClick={() => openEditModal(record)}>
            {t("editBtn")}
          </Button>
          <Button
            size="small"
            type="primary"
            onClick={() => {
              setIsModalOpen(true);
              setPrintData(record);
            }}
          >
            {t("print")}
          </Button>
        </Space>
      ),
    },
  ];  

  return (
    <>
      <Header />

      <div className="px-6 min-h-[550px]">
        <h1 className="text-2xl text-center font-semibold mb-7 text-gray-800">{t("invoicesPageTitle")}</h1>

        {isPosUser && (
          <div className="mb-4 flex flex-wrap items-center justify-between gap-4">
            <div className="flex flex-wrap gap-3 items-center">
              <Button
                type="primary"
                onClick={handleGenerateWeeklyPosInvoices}
                loading={weeklyLoading}
              >
                {t("weeklyLast7Days")}
              </Button>
              <Button
                type="primary"
                onClick={handleGenerateMonthlyPosInvoices}
                loading={monthlyLoading}
              >
                {t("monthlyLast30Days")}
              </Button>
            </div>
            <div className="flex flex-wrap gap-3 items-center">
              <span className="text-xs text-gray-600 whitespace-nowrap">{t("dateFrom")}</span>
              <DatePicker
                value={customFrom}
                onChange={(v) => setCustomFrom(v)}
                disabledDate={(current) => current && current > dayjs().endOf("day")}
              />
              <span className="text-xs text-gray-600 whitespace-nowrap">{t("dateTo")}</span>
              <DatePicker
                value={customTo}
                onChange={(v) => setCustomTo(v)}
                disabledDate={(current) => current && current > dayjs().endOf("day")}
              />
              <Button
                type="default"
                onClick={handleGenerateCustomPosInvoices}
                loading={customLoading}
              >
                {t("generateCustomRange")}
              </Button>
            </div>
          </div>
        )}

        <Table
          dataSource={invoices}
          columns={columns}
          bordered
          rowKey="_id"
          loading={loading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showTotal: (total, range) =>
              t("invoicesPaginationRange", { from: range[0], to: range[1], total }),
          }}
          onChange={(pager) => {
            getInvoices(pager.current, pager.pageSize, invoiceNumberSearch);
          }}
          scroll={{ x: 1200, y: 490 }}
          locale={{ emptyText: t("noInvoicesFound") }}
        />
      </div>

      <PrintInvoice
        isModalOpen={isModalOpen}
        setIsModalOpen={(open) => {
          setIsModalOpen(open);
          // Refresh invoice list when print modal closes after auto-print
          if (!open && location.state?.autoPrint && autoOpenedRef.current) {
            // Clear the autoPrint state to prevent re-triggering
            window.history.replaceState({}, document.title);
            // Refresh the list to show the latest invoice
            getInvoices(pagination.current, pagination.pageSize, invoiceNumberSearch, true);
          }
        }}
        printData={printData}
      />

      <PrintPosPeriod
        isModalOpen={posPeriodPrintOpen}
        setIsModalOpen={setPosPeriodPrintOpen}
        periodLabel={posPeriodLabel}
        fromISO={posPeriodFromISO}
        toISO={posPeriodToISO}
        invoices={posPeriodInvoices}
      />

      <Modal
        title={t("selectInvoiceType")}
        open={fbrFilterModalOpen}
        onOk={handleFbrFilterConfirm}
        onCancel={handleFbrFilterCancel}
        okText={t("generate")}
        cancelText={t("close")}
        width={500}
      >
        <div className="py-4">
          <p className="mb-4 text-gray-700">
            {t("fbrReportIncludePrompt")}
          </p>
          <Radio.Group 
            value={selectedFbrFilter} 
            onChange={(e) => setSelectedFbrFilter(e.target.value)}
            className="w-full"
          >
            <Space direction="vertical" size="middle" className="w-full">
              <Radio value="fbr" className="block">
                <div>
                  <div className="font-medium">{t("fbrInvoicesOnly")}</div>
                  <div className="text-xs text-gray-500">{t("fbrInvoicesOnlyDesc")}</div>
                </div>
              </Radio>
              <Radio value="nonFbr" className="block">
                <div>
                  <div className="font-medium">{t("nonFbrInvoicesOnly")}</div>
                  <div className="text-xs text-gray-500">{t("nonFbrInvoicesOnlyDesc")}</div>
                </div>
              </Radio>
              <Radio value="both" className="block">
                <div>
                  <div className="font-medium">{t("bothFbrNonFbr")}</div>
                  <div className="text-xs text-gray-500">{t("bothFbrNonFbrDesc")}</div>
                </div>
              </Radio>
            </Space>
          </Radio.Group>
        </div>
      </Modal>

      <Modal
        title={t("editInvoice")}
        open={isEditModalOpen}
        onCancel={() => {
          setIsEditModalOpen(false);
          setEditingInvoice(null);
          setEditItems([]);
          editForm.resetFields();
        }}
        onOk={handleEditSubmit}
        okText={t("saveChanges")}
        confirmLoading={loading}
        width={800}
        destroyOnHidden
      >
        <Form
          form={editForm}
          layout="vertical"
        >
          {/* Basic meta */}
          <Divider orientation="left">Invoice Details</Divider>
          <div className="grid md:grid-cols-2 gap-4">
            <Form.Item label={t("invoiceNumber")} name="invoiceNumber">
              <Input disabled />
            </Form.Item>
            <Form.Item
              label={t("invoiceDate")}
              name="invoiceDate"
            >
              <DatePicker className="w-full" />
            </Form.Item>
            <Form.Item
              label={t("dueDate")}
              name="dueDate"
            >
              <DatePicker className="w-full" />
            </Form.Item>
            <Form.Item
              label={t("paymentMethod")}
              name="paymentMode"
              rules={[{ required: true, message: t("paymentMethodRequired") }]}
            >
              <Select
                placeholder={t("paymentPlaceholder")}
                options={[
                  { value: "Cash", label: "Cash" },
                  { value: "Debit Card", label: "Debit Card" },
                  { value: "Credit", label: "Credit" },
                ]}
              />
            </Form.Item>
          </div>

          {/* Customer basics */}
          <Divider orientation="left">{t("customer")}</Divider>
          <div className="grid md:grid-cols-2 gap-4">
            <Form.Item
              label={t("customerName")}
              name="customerName"
            >
              <Input placeholder={t("customerNamePlaceholder")} />
            </Form.Item>
            <Form.Item
              label={t("customerPhoneNumber")}
              name="customerPhoneNumber"
            >
              <Input placeholder={t("customerPhonePlaceholder")} maxLength={11} />
            </Form.Item>
            <Form.Item
              label={t("customerAddress")}
              name="customerAddress"
            >
              <Input.TextArea rows={2} placeholder={t("completeAddress")} />
            </Form.Item>
          </div>

          <Form.Item
            label={t("editReasonRequired")}
            name="editReason"
            rules={[
              {
                required: true,
                message: t("editReasonRequiredMessage"),
              },
              {
                validator: (_, value) => {
                  const trimmed = value ? String(value).trim() : "";
                  if (!trimmed) {
                    return Promise.reject(new Error(t("editReasonNotEmpty")));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input.TextArea
              rows={2}
              placeholder={t("editReasonPlaceholder")}
              maxLength={70}
              showCount
            />
          </Form.Item>

          {/* Items & quantities */}
          <Divider orientation="left">{t("itemsAndQuantities")}</Divider>
          {Array.isArray(editItems) && editItems.length > 0 ? (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {editItems.map((item, index) => (
                <div
                  key={item._id || index}
                  className="flex items-center gap-3 border border-gray-100 rounded-md px-3 py-2"
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-800 truncate">
                      {item.title || t("unnamedItem")}
                    </div>
                    <div className="text-xs text-gray-500">
                      {t("unitPriceLabel")} {item.price ?? 0}{" "}
                      {item.uom ? `· ${item.uom}` : ""}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500">{t("qty")}</span>
                    <InputNumber
                      min={0}
                      step={1}
                      value={item.quantity}
                      className="!w-20"
                      onChange={(val) => {
                        const qty = Number(val) || 0;
                        setEditItems((prev) =>
                          prev.map((it, idx) => {
                            if (idx !== index) return it;
                            const price = Number(it.price) || 0;
                            const taxRate = Number(it.taxRate) || 0;
                            const discountPct = Number(it.discount) || 0;
                            const lineSub = price * qty;
                            const lineSubAfterDiscount = lineSub - (lineSub * discountPct) / 100;
                            const tax = taxRate > 0 ? (lineSubAfterDiscount * taxRate) / 100 : (Number(it.tax) || 0);
                            return { ...it, quantity: qty, tax };
                          })
                        );
                      }}
                    />
                    <Button
                      size="small"
                      danger
                      onClick={() => {
                        setEditItems((prev) =>
                          prev.filter((_, idx) => idx !== index)
                        );
                      }}
                    >
                      {t("remove")}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-xs text-gray-500 mb-2">
              {t("noItemsToEdit")}
            </div>
          )}

          {/* Recalculated total when qty changes */}
          {Array.isArray(editItems) && editItems.length > 0 && (() => {
            const totals = getEditTotals(editItems.filter((i) => (Number(i.quantity) || 0) > 0));
            return (
              <div className="mt-3 p-3 bg-gray-50 rounded-md border border-gray-100">
                <div className="text-xs font-medium text-gray-600 mb-1">{t("recalculatedBill")}</div>
                <div className="flex justify-between text-sm">
                  <span>{t("subtotal")}</span>
                  <span>{totals.subTotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>{t("tax")}</span>
                  <span>{totals.tax.toFixed(2)}</span>
                </div>
                <div className="flex justify-between font-semibold text-gray-800 mt-1 pt-1 border-t border-gray-200">
                  <span>{t("total")}</span>
                  <span>{totals.totalAmount.toFixed(2)}</span>
                </div>
              </div>
            );
          })()}

          {/* Digital / tax details – shown only when invoice is DIGITAL_INVOICING */}
          {editingInvoice?.invoiceType === INVOICE_TYPE_DIGITAL_INVOICING && (
            <>
              <Divider orientation="left">{t("taxAndRegistration")}</Divider>
              <div className="grid md:grid-cols-2 gap-4">
                <Form.Item
                  label={t("cnicNtn")}
                  name="customerCnicOrNtn"
                >
                  <Input placeholder={t("cnicPlaceholder")} maxLength={13} />
                </Form.Item>
                <Form.Item
                  label={t("businessName")}
                  name="businessName"
                >
                  <Input placeholder={t("businessNamePlaceholder")} />
                </Form.Item>
                <Form.Item
                  label={t("salesTaxNumber")}
                  name="salesTaxNumber"
                >
                  <Input placeholder={t("salesTaxNumberPlaceholder")} />
                </Form.Item>
                <Form.Item
                  label={t("province")}
                  name="province"
                >
                  <Select
                    allowClear
                    placeholder={t("selectProvinceRequired")}
                    options={PROVINCES}
                  />
                </Form.Item>
                <Form.Item
                  label={t("salesTaxRegistration")}
                  name="salesTaxRegistration"
                >
                  <Select allowClear placeholder={t("registeredUnregistered")}>
                    <Select.Option value="Registered">{t("registered")}</Select.Option>
                    <Select.Option value="Unregistered">{t("unregistered")}</Select.Option>
                  </Select>
                </Form.Item>
                <Form.Item
                  label={t("challanNumber")}
                  name="challanNumber"
                >
                  <Input placeholder={t("challanOptional")} />
                </Form.Item>
              </div>
            </>
          )}

          {editingInvoice?.lastEditedAt && (
            <div className="mt-4 text-xs text-gray-500">
              <Tag color="blue">
                {t("lastEditedLabel")}&nbsp;
                {new Date(editingInvoice.lastEditedAt).toLocaleString("en-PK")}
              </Tag>
            </div>
          )}
        </Form>
      </Modal>
    </>
  );
};

export default InvoicePage;
